import * as productNames from '../../consts/product/productNames';
import codentifyTransformService from '../codentifyTransformService';
import deviceTypesService from '../device/deviceTypesService';

const getVariableName = (variableObj) => Object.keys(variableObj)[0];

const getIdentificatorObject = (variableObj) => {
    const identificatorName = getVariableName(variableObj);
    const identificatorValue = variableObj[identificatorName];

    return {
        identificatorName,
        identificatorValue,
    };
};

const getProductIdentificator = (product) => {
    const {codentify, deviceSerialNumber} = product;
    const identificatorObject = deviceSerialNumber
        ? getIdentificatorObject({deviceSerialNumber})
        : getIdentificatorObject({codentify});

    return identificatorObject;
};

const getValidProducts = (products) => {
    return products?.filter((x) => !!x) || [];
};

const findProduct = ({products, deviceSerialNumber, codentify}) => {
    if (!deviceSerialNumber && !codentify) return;

    const {identificatorName, identificatorValue} = getProductIdentificator({
        deviceSerialNumber,
        codentify,
    });

    return getValidProducts(products).find((product) => product[identificatorName] === identificatorValue);
};

const forceFindProduct = ({products, deviceSerialNumber, codentify}) => {
    const productBySerialNumber = findProduct({products, deviceSerialNumber});
    const productByCodentify = findProduct({products, codentify});
    const transformedCodentify = codentifyTransformService.transformCodentifyForIcc(codentify);
    const productByTransformedCodentify = findProduct({products, codentify: transformedCodentify});

    return productBySerialNumber || productByCodentify || productByTransformedCodentify;
};

const someProduct = ({products, deviceSerialNumber, codentify}) => {
    const {identificatorName, identificatorValue} = getProductIdentificator({
        deviceSerialNumber,
        codentify,
    });

    return getValidProducts(products).some((product) => product[identificatorName] === identificatorValue);
};

const filterProduct = ({products, deviceSerialNumber, codentify}) => {
    const {identificatorName, identificatorValue} = getProductIdentificator({
        deviceSerialNumber,
        codentify,
    });

    return getValidProducts(products).filter((product) => product[identificatorName] !== identificatorValue);
};

const getNewProductsListWithUpdatedItem = ({products, deviceSerialNumber, codentify, data}) => {
    const isProductExist = someProduct({
        products,
        deviceSerialNumber,
        codentify,
    });

    let newList = products?.length ? [...products] : [];

    if (isProductExist) {
        newList = newList.map((product) => {
            if (product.deviceSerialNumber === deviceSerialNumber || product.codentify === codentify) {
                return {...product, ...data};
            }

            return product;
        });
    } else {
        newList.push({
            deviceSerialNumber,
            codentify,
            ...data,
        });
    }

    return newList;
};

const isOriginalCodentifyValid = (codentifyID) => {
    return codentifyID && !codentifyID.includes('*');
};

const getTransformedDeviceSerialNumber = (deviceSerialNumber) => {
    if (deviceSerialNumber) {
        const upperCaseSerialNumber = deviceSerialNumber.toUpperCase();
        const isDusnFormat = upperCaseSerialNumber.startsWith('0X');

        if (isDusnFormat) {
            const tranformedSerialNumber =
                '0' + upperCaseSerialNumber.charAt(1).toLowerCase() + upperCaseSerialNumber.slice(2);

            return tranformedSerialNumber;
        }
    }

    return deviceSerialNumber;
};

const isUseProductBrandDifferentiator = (deviceType, color) => {
    const isP1V41 =
        deviceTypesService.isHermesPrime(deviceType) ||
        deviceTypesService.isHermesMid(deviceType) ||
        deviceTypesService.isHermesBeta(deviceType);

    if (isP1V41) return true;

    const isP1V40 = deviceTypesService.isIluma(deviceType) || deviceTypesService.isIlumaHolder(deviceType);

    return isP1V40 && (color === productNames.PRODUCT_COLOR_NEP || color === productNames.PRODUCT_COLOR_NVI);
};

export default {
    getProductIdentificator,
    findProduct,
    forceFindProduct,
    someProduct,
    filterProduct,
    getNewProductsListWithUpdatedItem,
    isOriginalCodentifyValid,
    getTransformedDeviceSerialNumber,
    isUseProductBrandDifferentiator,
};
